import {
  ActionIcon,
  Badge,
  Card,
  Divider,
  Group,
  Loader,
  Pagination,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from "@mantine/core"
import {
  IconAlertTriangle,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconInfoCircle,
  IconX,
} from "@tabler/icons-react"
import { useMemo, useState } from "react"

import { BudgetsResponses } from "@costory/types/endpoints/budgets"

import dayjs from "@costory/shared/dayjs"

import { useGetBudgetNotificationsQuery } from "@costory/front/queries/budgets"

interface BudgetNotificationsTabProps {
  budgetId: string
}

export const BudgetNotificationsTab = ({
  budgetId,
}: BudgetNotificationsTabProps) => {
  const query = useGetBudgetNotificationsQuery(budgetId)
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({})
  const [page, setPage] = useState(1)
  const pageSize = 5

  const notifications = useMemo(() => query.data || [], [query.data])
  const totalPages = Math.max(1, Math.ceil(notifications.length / pageSize))
  const currentPage = Math.min(page, totalPages)

  const paginatedNotifications = useMemo(
    () =>
      notifications.slice(
        (currentPage - 1) * pageSize,
        (currentPage - 1) * pageSize + pageSize,
      ),
    [notifications, currentPage, pageSize],
  )

  if (query.isLoading) {
    return (
      <Stack align="center" py="xl">
        <Loader size="lg" type="dots" />
      </Stack>
    )
  }

  if (query.isError || !query.data) {
    return (
      <Text color="red" ta="center" py="xl">
        Failed to load notifications.
      </Text>
    )
  }

  if (query.data.length === 0) {
    return (
      <Text color="dimmed" ta="center" py="xl">
        ✨ No notifications sent for this budget yet.
      </Text>
    )
  }

  const getAlertColor = (level: string) => {
    switch (level) {
      case "RED":
        return "red"
      case "ORANGE":
        return "orange"
      case "YELLOW":
        return "yellow"
      case "GREEN":
        return "teal"
      default:
        return "gray"
    }
  }

  const getAlertEmoji = (level: string) => {
    switch (level) {
      case "RED":
        return "🚨"
      case "ORANGE":
        return "⚠️"
      case "YELLOW":
        return "⚡"
      case "GREEN":
        return "✅"
      default:
        return "ℹ️"
    }
  }

  const getStatusColor = (ack: boolean) => (ack ? "teal" : "orange.7")

  const getPeriodColor = (period?: string | null) => {
    switch (period?.toLowerCase()) {
      case "monthly":
        return "blue"
      case "weekly":
        return "grape"
      case "daily":
        return "indigo"
      case "yearly":
        return "cyan"
      default:
        return "gray"
    }
  }

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const formatCurrency = (
    value: number | null | undefined,
    currency: string,
  ) => {
    const numericValue = typeof value === "number" ? value : 0
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 2,
    }).format(numericValue)
  }

  return (
    <Stack gap="md" py="md">
      <Stack gap="sm">
        {paginatedNotifications.map(
          (notification: BudgetsResponses.GetNotifications[number]) => {
            const metadata =
              (notification.metadata as BudgetsResponses.BudgetAlertMetadata) ||
              null
            const isAcknowledged = !!notification.acknowledgedAt
            const alertLevel = metadata?.alertLevel || "YELLOW"
            const isExpanded = expandedIds[notification.id]

            const currency = metadata?.currency || "USD"
            const forecast = metadata?.forecastedCost ?? 0
            const budget = metadata?.monthlyBudget ?? 0
            const currentSpend = metadata?.currentCost ?? 0
            const percentage = metadata?.percentage ?? 0

            return (
              <Card
                key={notification.id}
                withBorder
                radius="md"
                shadow="sm"
                p="md"
                bg="var(--mantine-color-body)"
              >
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                  <Stack gap={6} miw={0}>
                    <Group gap="xs">
                      <Badge
                        color={getAlertColor(alertLevel)}
                        variant="filled"
                        radius="sm"
                        size="sm"
                        leftSection={getAlertEmoji(alertLevel)}
                      >
                        {alertLevel}
                      </Badge>
                      <Text fw={600} size="lg" truncate>
                        💼{" "}
                        {metadata?.team || notification.alert?.name || "Budget"}
                      </Text>
                    </Group>

                    <Group gap="xs" wrap="wrap">
                      <Badge
                        leftSection={
                          isAcknowledged ? (
                            <IconCheck size="0.8rem" />
                          ) : (
                            <IconX size="0.8rem" />
                          )
                        }
                        color={getStatusColor(isAcknowledged)}
                        variant="light"
                        radius="sm"
                        size="sm"
                      >
                        {isAcknowledged
                          ? `✅ Acknowledged by ${notification.acknowledgedBy?.firstName ?? "team"}`
                          : "⏳ Pending"}
                      </Badge>
                      <Badge
                        color={getPeriodColor(notification.alert?.period)}
                        variant="light"
                        radius="sm"
                        size="sm"
                      >
                        📅 {notification.alert?.period ?? "Monthly"}
                      </Badge>
                      <Tooltip label="Computation time" position="bottom">
                        <Text size="sm" c="dimmed">
                          🕐{" "}
                          {dayjs(notification.computedAt).format(
                            "MMM D, YYYY [at] h:mm A",
                          )}
                        </Text>
                      </Tooltip>
                    </Group>
                  </Stack>

                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    onClick={() => toggleExpanded(notification.id)}
                    aria-label="Toggle details"
                  >
                    {isExpanded ? (
                      <IconChevronUp size={18} />
                    ) : (
                      <IconChevronDown size={18} />
                    )}
                  </ActionIcon>
                </Group>

                {isExpanded ? (
                  <>
                    <Divider my="sm" />
                    <Stack gap="sm">
                      <Group gap="xs">
                        <ThemeIcon size="sm" variant="light" color="blue">
                          <IconInfoCircle size={16} />
                        </ThemeIcon>
                        <Text fw={700} size="sm">
                          🔍 Root Cause Analysis
                        </Text>
                      </Group>
                      <Stack gap={4}>
                        <Text
                          size="xs"
                          fw={700}
                          color="dimmed"
                          style={{ letterSpacing: "0.5px" }}
                        >
                          THE ISSUE
                        </Text>
                        <Text size="sm">
                          {forecast > budget ? (
                            <>
                              Cost will increase from{" "}
                              <Text span fw={700}>
                                {formatCurrency(budget, currency)}
                              </Text>{" "}
                              to{" "}
                              <Text span fw={700}>
                                {formatCurrency(forecast, currency)}
                              </Text>
                              , exceeding the monthly budget.
                            </>
                          ) : (
                            <>
                              Cost is projected to reach{" "}
                              <Text span fw={700}>
                                {formatCurrency(forecast, currency)}
                              </Text>
                              , approaching the monthly budget of{" "}
                              <Text span fw={700}>
                                {formatCurrency(budget, currency)}
                              </Text>
                              .
                            </>
                          )}
                        </Text>
                      </Stack>

                      <Group gap="lg" align="stretch" wrap="wrap">
                        <Stack gap={4}>
                          <Text size="xs" c="dimmed" fw={700} tt="uppercase">
                            📊 Usage
                          </Text>
                          <Text size="sm">
                            Currently at{" "}
                            <Text span fw={800} c={getAlertColor(alertLevel)}>
                              📈 {percentage.toFixed(1)}%
                            </Text>{" "}
                            of total budget.
                          </Text>
                          <Text size="xs" c="dimmed">
                            💸 Current spend:{" "}
                            {formatCurrency(currentSpend, currency)}
                          </Text>
                        </Stack>

                        <Stack gap={4}>
                          <Text size="xs" c="dimmed" fw={700} tt="uppercase">
                            ⚠️ Forecast vs Budget
                          </Text>
                          <Group gap="sm">
                            <ThemeIcon
                              size="sm"
                              variant="light"
                              color={getAlertColor(alertLevel)}
                            >
                              <IconAlertTriangle size={14} />
                            </ThemeIcon>
                            <Stack gap={2}>
                              <Text fw={600}>
                                {forecast > budget ? (
                                  <>
                                    🚨{" "}
                                    {formatCurrency(
                                      forecast - budget,
                                      currency,
                                    )}{" "}
                                    over budget forecast
                                  </>
                                ) : (
                                  <>
                                    ⚠️{" "}
                                    {formatCurrency(
                                      budget - forecast,
                                      currency,
                                    )}{" "}
                                    remaining before budget limit
                                  </>
                                )}
                              </Text>
                              <Text size="xs" c="dimmed">
                                Projected Overrun:{" "}
                                <Text
                                  span
                                  fw={700}
                                  c={
                                    forecast > budget
                                      ? getAlertColor(alertLevel)
                                      : "blue"
                                  }
                                >
                                  {forecast > budget ? "+" : ""}
                                  {(
                                    ((forecast - budget) / budget) *
                                    100
                                  ).toFixed(1)}
                                  %
                                </Text>
                              </Text>
                            </Stack>
                          </Group>
                        </Stack>
                      </Group>
                    </Stack>
                  </>
                ) : (
                  <Text mt="xs" size="sm" c="dimmed">
                    📊 Forecast 💵 {formatCurrency(forecast, currency)} vs
                    budget 💰 {formatCurrency(budget, currency)}; usage 📈{" "}
                    {percentage.toFixed(1)}% (💸{" "}
                    {formatCurrency(currentSpend, currency)})
                  </Text>
                )}
              </Card>
            )
          },
        )}
      </Stack>

      {query.data.length > pageSize && (
        <Group justify="center" mt="sm">
          <Pagination
            value={currentPage}
            onChange={setPage}
            total={totalPages}
            size="sm"
            radius="md"
          />
        </Group>
      )}
    </Stack>
  )
}
